import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Icon from '@mdi/react';
import { mdiArrowLeft, mdiContentSave } from '@mdi/js';
import { apiService, RfqSubmission } from '../../services/apiService';
import { StatusBadge } from '../components/StatusBadge';
import { useToast } from '../components/Toast';
import { adminTheme } from '../styles/adminTheme';

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const BackBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${adminTheme.colors.textSecondary};
  font-size: 13px;
  padding: 4px 8px;
  border-radius: 4px;

  &:hover {
    background: #f1f3f4;
  }
`;

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: ${adminTheme.colors.text};
  flex: 1;
`;

const SaveBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${adminTheme.colors.primary};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: ${adminTheme.colors.primaryHover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${adminTheme.colors.cardBg};
  border: 1px solid ${adminTheme.colors.border};
  border-radius: ${adminTheme.borderRadius};
  padding: 20px;
`;

const CardTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${adminTheme.colors.text};
  margin-bottom: 16px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid ${adminTheme.colors.border};
  font-size: 13px;

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: ${adminTheme.colors.textSecondary};
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: ${adminTheme.colors.text};
`;

const FormGroup = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: ${adminTheme.colors.textSecondary};
  margin-bottom: 4px;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${adminTheme.colors.border};
  border-radius: 4px;
  font-size: 13px;
  color: ${adminTheme.colors.text};
  background: white;
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${adminTheme.colors.border};
  border-radius: 4px;
  font-size: 13px;
  color: ${adminTheme.colors.text};
  min-height: 100px;
  resize: vertical;
`;

const MessageBox = styled.div`
  padding: 12px;
  background: ${adminTheme.colors.rowAlt};
  border-radius: 6px;
  font-size: 13px;
  color: ${adminTheme.colors.text};
  line-height: 1.5;
  white-space: pre-wrap;
`;

const Loading = styled.div`
  text-align: center;
  padding: 40px;
  color: ${adminTheme.colors.textMuted};
`;

const statusOptions = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];

export const RfqDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [rfq, setRfq] = useState<RfqSubmission | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    apiService.getRfq(Number(id))
      .then(data => {
        setRfq(data);
        setStatus(data.status);
        setNotes(data.notes);
      })
      .catch(() => toast('Failed to load RFQ', 'error'))
      .finally(() => setLoading(false));
  }, [id, toast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await apiService.updateRfq(Number(id), { status, notes });
      setRfq(updated);
      toast('RFQ updated', 'success');
    } catch {
      toast('Failed to update RFQ', 'error');
    }
    setSaving(false);
  };

  if (loading || !rfq) return <Loading>Loading...</Loading>;

  const nodes = rfq.circuit_schema?.nodes?.length ?? 0;
  const edges = rfq.circuit_schema?.edges?.length ?? 0;

  return (
    <div>
      <Header>
        <BackBtn onClick={() => navigate('/panel/rfq')}>
          <Icon path={mdiArrowLeft} size={0.65} />
          RFQ Pipeline
        </BackBtn>
        <PageTitle>RFQ: {rfq.name}</PageTitle>
        <SaveBtn onClick={handleSave} disabled={saving}>
          <Icon path={mdiContentSave} size={0.6} />
          {saving ? 'Saving...' : 'Save'}
        </SaveBtn>
      </Header>

      <Grid>
        <div>
          <Card style={{ marginBottom: 20 }}>
            <CardTitle>Contact Information</CardTitle>
            <InfoRow>
              <InfoLabel>Name</InfoLabel>
              <InfoValue>{rfq.name}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Company</InfoLabel>
              <InfoValue>{rfq.company}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Email</InfoLabel>
              <InfoValue>{rfq.email}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Phone</InfoLabel>
              <InfoValue>{rfq.phone || '—'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Submitted</InfoLabel>
              <InfoValue>{new Date(rfq.created_at).toLocaleString()}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Status</InfoLabel>
              <InfoValue><StatusBadge status={rfq.status} /></InfoValue>
            </InfoRow>
          </Card>

          <Card>
            <CardTitle>Message</CardTitle>
            <MessageBox>{rfq.message || 'No message provided'}</MessageBox>
          </Card>
        </div>

        <div>
          <Card style={{ marginBottom: 20 }}>
            <CardTitle>Circuit Summary</CardTitle>
            <InfoRow>
              <InfoLabel>Components</InfoLabel>
              <InfoValue>{nodes}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Connections</InfoLabel>
              <InfoValue>{edges}</InfoValue>
            </InfoRow>
          </Card>

          <Card>
            <CardTitle>Manage</CardTitle>
            <FormGroup>
              <Label>Status</Label>
              <Select value={status} onChange={e => setStatus(e.target.value)}>
                {statusOptions.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Internal Notes</Label>
              <Textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Add internal notes about this RFQ..."
              />
            </FormGroup>
          </Card>
        </div>
      </Grid>
    </div>
  );
};
